/**
 * @fileoverview DeviceMockup component for realistic device frame rendering
 * @module device-mockup/components/DeviceMockup
 *
 * Renders a realistic device frame (iPhone 14 Pro, Android, or Desktop)
 * around screen content with auto-scaling.
 */

import React, { memo, useRef } from 'react';
import type { ReactNode, CSSProperties } from 'react';
import { useScreenScale } from '../../hooks/useScreenScale';
import { SafariTopBar } from '../SafariTopBar';
import { SafariBottomBar } from '../SafariBottomBar';
import styles from './DeviceMockup.module.css';

export type DeviceType = 'iphone' | 'android' | 'tablet' | 'laptop' | 'desktop';

/** Frame-less devices rendered as a plain scaled container (no bezel art). */
const FLAT_DEVICES: ReadonlySet<DeviceType> = new Set(['tablet', 'laptop', 'desktop']);

export interface DeviceMockupScaleOptions {
  padding?: number;
  minScale?: number;
  maxScale?: number;
}

export interface DeviceMockupProps {
  device?: DeviceType;
  children: ReactNode;
  className?: string;
  'data-testid'?: string;
  autoScale?: boolean;
  scaleOptions?: DeviceMockupScaleOptions;
  showSafariBars?: boolean;
}

export const DEVICE_CONFIG = {
  iphone: {
    mockupWidth: 430,
    mockupHeight: 932,
    screenWidth: 430,
    screenHeight: 932,
    borderRadius: 47,
    bezelSize: 6,
    hasDynamicIsland: true,
  },
  android: {
    mockupWidth: 432,
    mockupHeight: 935,
    screenWidth: 412,
    screenHeight: 915,
    borderRadius: 40,
    bezelSize: 10,
    hasDynamicIsland: false,
  },
  tablet: {
    mockupWidth: 820,
    mockupHeight: 1180,
    screenWidth: 820,
    screenHeight: 1180,
    borderRadius: 0,
    bezelSize: 0,
    hasDynamicIsland: false,
  },
  laptop: {
    mockupWidth: 1280,
    mockupHeight: 832,
    screenWidth: 1280,
    screenHeight: 832,
    borderRadius: 0,
    bezelSize: 0,
    hasDynamicIsland: false,
  },
  desktop: {
    mockupWidth: 1440,
    mockupHeight: 900,
    screenWidth: 1440,
    screenHeight: 900,
    borderRadius: 0,
    bezelSize: 0,
    hasDynamicIsland: false,
  },
} as const;

export const DeviceMockup = memo(function DeviceMockup({
  device = 'iphone',
  children,
  className,
  'data-testid': testId,
  autoScale = true,
  scaleOptions,
  showSafariBars = false,
}: DeviceMockupProps): React.ReactElement {
  const containerRef = useRef<HTMLDivElement>(null);
  const config = DEVICE_CONFIG[device];

  const { scale, isReady } = useScreenScale(containerRef, {
    padding: scaleOptions?.padding ?? 32,
    minScale: scaleOptions?.minScale ?? 0.1,
    maxScale: scaleOptions?.maxScale ?? 1.0,
    contentWidth: config.mockupWidth,
    contentHeight: config.mockupHeight,
  });

  if (FLAT_DEVICES.has(device)) {
    const desktopStyle: CSSProperties = autoScale
      ? {
          width: config.mockupWidth,
          height: config.mockupHeight,
          transform: `scale(${scale})`,
          transformOrigin: 'top center',
          visibility: isReady ? 'visible' : 'hidden',
        }
      : {};

    return (
      <div
        ref={containerRef}
        className={`${styles.desktopContainer} ${className || ''}`}
        data-testid={testId}
      >
        <div style={desktopStyle}>{children}</div>
      </div>
    );
  }

  const mockupStyle: CSSProperties = {
    width: config.mockupWidth,
    height: config.mockupHeight,
    borderRadius: config.borderRadius,
    padding: config.bezelSize,
    ...(autoScale && {
      transform: `scale(${scale})`,
      transformOrigin: 'top center',
      visibility: isReady ? 'visible' : 'hidden',
    }),
  };

  return (
    <div
      ref={containerRef}
      className={`${styles.container} ${className || ''}`}
      data-testid={testId}
    >
      <div className={styles.mockup} style={mockupStyle}>
        {config.hasDynamicIsland && (
          <>
            <div className={styles.silentSwitch} />
            <div className={styles.volumeUp} />
            <div className={styles.volumeDown} />
          </>
        )}

        {config.hasDynamicIsland && (
          <div
            className={styles.dynamicIsland}
            style={{ top: config.bezelSize + 8 }}
            data-testid={testId ? `${testId}-dynamic-island` : undefined}
          />
        )}

        <div
          className={styles.screen}
          data-testid={testId ? `${testId}-screen` : undefined}
          style={{
            borderRadius: config.borderRadius - config.bezelSize,
            ...(showSafariBars ? {
              '--sv2-device-padding-top': '0px',
            } as CSSProperties : {}),
          }}
        >
          {showSafariBars && <SafariTopBar />}
          <div className={styles.screenContent}>
            {children}
          </div>
          {showSafariBars && <SafariBottomBar />}
        </div>
      </div>
    </div>
  );
});

DeviceMockup.displayName = 'DeviceMockup';
